import { getInternalAppSdk } from '@/utils/internalAppSdk'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { SECOND_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import type { CctpContextResponse } from '@vaultsfyi/common/schemas'
import type { Network } from '@vaultsfyi/common/types'
import type { Address } from 'viem'

export const CCTP_USDC_ADDRESSES: Partial<Record<Network, Address>> = {
  mainnet: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
  arbitrum: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831',
  base: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
}

interface CctpContextArgs {
  userAddress: Address | undefined
  srcNetwork: Network
  dstNetwork: Network
}

const cctpContextQueryKey = ['cctpContext'] as const

const cctpContextQueryOptions = ({ userAddress, srcNetwork, dstNetwork }: CctpContextArgs) => {
  const assetAddress = CCTP_USDC_ADDRESSES[srcNetwork]

  return queryOptions({
    queryKey: [...cctpContextQueryKey, { userAddress, srcNetwork, dstNetwork }] as const,
    queryFn(): Promise<CctpContextResponse> {
      const internalAppSdk = getInternalAppSdk()

      return internalAppSdk.getCctpContext({
        userAddress: userAddress!,
        srcNetwork,
        dstNetwork,
        assetAddress: assetAddress!,
      })
    },
    enabled: !!userAddress && !!assetAddress && srcNetwork !== dstNetwork,
    retry: 1,
    staleTime: 15 * SECOND_IN_MILLISECONDS,
    refetchInterval: 10 * SECOND_IN_MILLISECONDS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  })
}

export function useCctpContextQuery({ userAddress, srcNetwork, dstNetwork }: CctpContextArgs) {
  return useQuery(cctpContextQueryOptions({ userAddress, srcNetwork, dstNetwork }))
}
